import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import {
	editGeneratableDocumentTemplateFields,
	getGeneratableDocumentTemplateFields,
} from '@dealstack/frontend/shared/dataExchange/services/god.service.ts';
import { useEffect } from 'react';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';

const SMART_FIELD_SHAPE = z.object({
	id: z.string(),
	fieldKey: z.string(),
	fieldSourceTemplate: z.string().nullish(),
	fieldSourceDescription: z.string().nullish(),
});

const FORM_SHAPE = z.object({
	smartFields: z.array(SMART_FIELD_SHAPE),
});

export type SmartFieldsFormValues = z.infer<typeof FORM_SHAPE>;
export type SmartFieldInForm = SmartFieldsFormValues['smartFields'][number];

export const useSmartFieldsController = (templateId: string) => {
	const getTemplateSmartFieldsRequest = useQuery({
		queryKey: [
			API_PATHS.GODMODE_GENERATABLE_DOCUMENTS_TEMPLATE_ID_SMART_FIELDS_GET,
			{ id: templateId },
		],
		queryFn: ({ signal }) =>
			getGeneratableDocumentTemplateFields(
				API_PATHS.GODMODE_GENERATABLE_DOCUMENTS_TEMPLATE_ID_SMART_FIELDS_GET,
				{ id: templateId },
				signal
			),
	});

	const smartFields = getTemplateSmartFieldsRequest.data ?? [];

	const editSmartFieldsRequest = useMutation({
		mutationFn: editGeneratableDocumentTemplateFields,
	});

	const form = useForm<SmartFieldsFormValues>({
		mode: 'all',
		resolver: zodResolver(FORM_SHAPE),
		defaultValues: { smartFields: [] },
	});

	useEffect(() => {
		if (!smartFields?.length) {
			return;
		}

		form.reset({
			smartFields: smartFields.map((field) => ({
				id: field.id,
				fieldKey: field.fieldKey,
				fieldSourceTemplate: field.fieldSourceTemplate,
				fieldSourceDescription: field.fieldSourceDescription,
			})),
		});
	}, [smartFields]);

	const onSmartFieldsSubmit = (formValues: SmartFieldsFormValues) => {
		if (!smartFields?.length) {
			return;
		}

		editSmartFieldsRequest.mutate(
			{
				smartFields: formValues.smartFields.map((field) => ({
					id: field.id,
					fieldSourceTemplate: field.fieldSourceTemplate || undefined,
					fieldSourceDescription: field.fieldSourceDescription || undefined,
				})),
			},
			{
				onSuccess: async () => {
					showToastV2({ title: 'Updated successfully' });
					await getTemplateSmartFieldsRequest.refetch();
				},
			}
		);
	};

	return {
		smartFields,
		smartFieldForm: form,
		onSmartFieldsSubmit: form.handleSubmit(onSmartFieldsSubmit),
	};
};
